import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  Badge,
  SwipeableDrawer,
  Divider,
  Fade,
  Chip,
} from '@mui/material';
import { 
  AccountCircle, 
  KeyboardArrowDown, 
  Menu as MenuIcon,
  ExpandLess,
  ExpandMore,
  Notifications,
  Home,
  Settings,
  Close
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthModule';
import { useTranslation } from 'react-i18next';
import BuildIcon from '@mui/icons-material/Build';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LogoutIcon from '@mui/icons-material/Logout';
import EventIcon from '@mui/icons-material/Event';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GroupsIcon from '@mui/icons-material/Groups';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ChatIcon from '@mui/icons-material/Chat';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import { motion, AnimatePresence } from 'framer-motion';
import { getTransformedImageUrl } from '../../utils/filestack';
import { notification as notificationApi, admin, message as messageApi, channel as channelApi, noticeboard } from '../../utils/api';
import socketManager from '../../utils/socketManager';
import { toast } from 'react-toastify';

const Navbar = ({ onSectionChange, activeSection }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [expandedSection, setExpandedSection] = React.useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const iOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadNoticesCount, setUnreadNoticesCount] = useState(0);
  const [unreadCommsCount, setUnreadCommsCount] = useState(0);
  const [unreadMentionsCount, setUnreadMentionsCount] = useState(0);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [siteSettings, setSiteSettings] = useState({
    logo: '/logo192.png',
    siteName: 'CAMATS TEAM RACING'
  });

  // Navigation sections configuration
  const SECTIONS = {
    pitbox: {
      id: 'pitbox',
      label: 'Pitbox',
      icon: <BuildIcon />,
      subsections: [
        { name: t('navigation.workshop'), route: '/workshop', enabled: true },
        { name: t('navigation.cars'), route: '/cars', enabled: true },
        { name: t('navigation.events'), route: '/events', enabled: true },
        { name: t('navigation.gallery'), route: '/galleries', enabled: true },
        { name: t('navigation.racetracks'), route: '/racetracks', enabled: true },
      ]
    }
  };

  // Set active section based on current URL path
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Find which section matches the current path
    const matchingSection = Object.entries(SECTIONS).find(([id, section]) => {
      // Check if the current path starts with the section route
      if (section.route && currentPath.startsWith(section.route)) {
        return true;
      }
      
      // Check if any subsection route matches the current path
      return section.subsections && section.subsections.some(subsection => 
        subsection.route && currentPath.startsWith(subsection.route)
      );
    });
    
    if (matchingSection) {
      const [sectionId] = matchingSection;
      
      // Set the expanded section for mobile view
      setExpandedSection(sectionId);
      
      // Set the active section for desktop view
      if (!isMobile && onSectionChange) {
        onSectionChange(sectionId);
      }
    }
  }, [location.pathname, isMobile, onSectionChange]);

  // Add useEffect to fetch unread count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (user) {
        try {
          const response = await notificationApi.getAll();
          setUnreadCount(response.unreadCount);
        } catch (error) {
          console.error('Error fetching unread notifications count:', error);
        }
      }
    };

    fetchUnreadCount();
    // Set up polling every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Add useEffect to fetch unread communications counts
  useEffect(() => {
    // Create a debounced version of the fetch function to prevent multiple rapid calls
    let fetchInProgress = false;
    let fetchTimeout = null;
    
    const fetchUnreadCommsCount = async () => {
      if (!user || !user._id || fetchInProgress) return;
      
      try {
        fetchInProgress = true;
        console.log('Fetching unread comms counts...');
        
        // Get user channels
        const response = await channelApi.getUserChannels();
        
        if (!response || !response.channels) {
          console.error('Invalid response from getUserChannels:', response);
          fetchInProgress = false;
          return;
        }
        
        const channels = [
          ...(response.teamChannels || []),
          ...(response.privateChannels || []),
          ...(response.lockedChannels || [])
        ];
        
        if (channels.length > 0) {
          const channelIds = channels.map(channel => channel._id);
          const unreadResponse = await messageApi.getUnreadCounts(user._id, channelIds);
          
          // Calculate total unread mentions and messages
          let totalUnreadMentions = 0;
          let totalUnreadMessages = 0;
          
          // Make sure we have counts and they're not null
          if (unreadResponse && unreadResponse.counts) {
            // Log individual channel counts for debugging
            console.log('Unread counts by channel:', unreadResponse.counts);
            
            Object.values(unreadResponse.counts).forEach(count => {
              if (count.unreadMentions > 0) {
                totalUnreadMentions += count.unreadMentions;
              }
              if (count.unreadMessages > 0) {
                // Only count messages that are not mentions to avoid double counting
                totalUnreadMessages += count.unreadMessages - (count.unreadMentions || 0);
              }
            });
          }
          
          console.log(`Unread communications: ${totalUnreadMessages} messages, ${totalUnreadMentions} mentions`);
          
          // Set total unread count (regular messages + mentions) and mentions separately
          setUnreadCommsCount(totalUnreadMessages + totalUnreadMentions);
          setUnreadMentionsCount(totalUnreadMentions);
        }
      } catch (error) {
        console.error('Error fetching unread communications count:', error);
      } finally {
        fetchInProgress = false;
      }
    };
    
    // Debounced fetch function to avoid multiple rapid calls
    const debouncedFetch = () => {
      if (fetchTimeout) {
        clearTimeout(fetchTimeout);
      }
      fetchTimeout = setTimeout(() => {
        fetchUnreadCommsCount();
      }, 300); // 300ms debounce time
    };
    
    // Only fetch if user is logged in
    if (user && user._id) {
      // Initial fetch
      fetchUnreadCommsCount();
      
      // Setup socket listeners for real-time updates with debouncing
      const handleNewMessage = () => debouncedFetch();
      const handleMention = () => debouncedFetch();
      const handleMessagesRead = () => debouncedFetch();
      const handleNewDirectMessage = () => debouncedFetch();
      
      // Register listeners
      socketManager.addEventListener('new-message', handleNewMessage);
      socketManager.addEventListener('mention', handleMention);
      socketManager.addEventListener('messages-read', handleMessagesRead);
      socketManager.addEventListener('new-direct-message', handleNewDirectMessage);
      
      // Set up polling with longer interval (every minute instead of 30 seconds)
      const interval = setInterval(fetchUnreadCommsCount, 60000);
      
      return () => {
        if (fetchTimeout) {
          clearTimeout(fetchTimeout);
        }
        clearInterval(interval);
        socketManager.removeEventListener('new-message', handleNewMessage);
        socketManager.removeEventListener('mention', handleMention);
        socketManager.removeEventListener('messages-read', handleMessagesRead);
        socketManager.removeEventListener('new-direct-message', handleNewDirectMessage);
      };
    }
  }, [user]);

  // Add useEffect to fetch site settings
  useEffect(() => {
    const fetchSiteSettings = async () => {
      try {
        const settings = await admin.getPublicSiteSettings();
        setSiteSettings(settings);
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }
    };

    fetchSiteSettings();
  }, []);

  // Add useEffect to fetch unread notices count
  useEffect(() => {
    const fetchUnreadNotices = async () => {
      if (user) {
        try {
          const response = await noticeboard.getUnreadCount();
          setUnreadNoticesCount(response.count || 0);
        } catch (error) {
          console.error('Error fetching unread notices count:', error);
        }
      }
    };

    fetchUnreadNotices();
    
    // Set up polling every 30 seconds
    const interval = setInterval(fetchUnreadNotices, 30000);
    
    // Set up event listener for notice read events
    const handleNoticeRead = () => {
      fetchUnreadNotices();
    };
    
    window.addEventListener('noticeRead', handleNoticeRead);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('noticeRead', handleNoticeRead);
    };
  }, [user]);

  // Update the totalUnreadCount whenever unreadCount or unreadNoticesCount changes
  useEffect(() => {
    setTotalUnreadCount(unreadCount + unreadNoticesCount);
  }, [unreadCount, unreadNoticesCount]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleSectionClick = (sectionId) => {
    const section = SECTIONS[sectionId];
    if (!isMobile) {
      if (activeSection === sectionId) {
        onSectionChange(null);
      } else {
        onSectionChange(sectionId);
      }
    } else {
      setExpandedSection(expandedSection === sectionId ? null : sectionId);
    }
    if (section.route) {
      navigate(section.route);
    }
  };

  const handleMobileSubsectionClick = (route) => {
    if (route) {
      navigate(route);
      setMobileOpen(false);
    }
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
    setUserMenuAnchor(null);
  };

  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    logout();
    navigate('/login');
    if (isMobile) setMobileOpen(false);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleNavigateToDashboard = () => {
    navigate('/dashboard');
    setUserMenuAnchor(null);
  };

  const handleLogoError = () => {
    console.error("Failed to load logo image");
    setLogoError(true);
  };

  const mobileDrawer = (
    <SwipeableDrawer
      disableBackdropTransition={!iOS}
      disableDiscovery={iOS}
      variant="temporary"
      anchor="left"
      open={mobileOpen}
      onOpen={() => setMobileOpen(true)}
      onClose={handleDrawerClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        '& .MuiDrawer-paper': { 
          width: '80%',
          maxWidth: 300,
          backgroundColor: 'rgba(18, 18, 18, 0.72)',
          backdropFilter: 'blur(10px)',
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          boxShadow: 3,
          zIndex: theme => theme.zIndex.drawer + 2
        },
        zIndex: theme => theme.zIndex.drawer + 2
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%' 
      }}>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'primary.main'
        }}>
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          >
            <Typography variant="h7" component={RouterLink} to="/" 
              sx={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {!logoError && (
                  <img 
                    src={siteSettings.logo} 
                    alt={siteSettings.siteName} 
                    style={{ height: 32, marginRight: 12 }} 
                    onError={handleLogoError}
                  />
                )}
                {siteSettings.siteName}
              </Box>
            </Typography>
          </motion.div>
          <IconButton onClick={handleDrawerClose} edge="end" color="inherit">
            <Close />
          </IconButton>
        </Box>
        {user && (
          <Box 
            component={RouterLink} 
            to="/dashboard"
            sx={{ 
              p: 2, 
              display: 'flex', 
              alignItems: 'center', 
              borderBottom: 1,
              borderColor: 'divider',
              textDecoration: 'none',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.05)'
              },
              transition: 'background-color 0.3s ease'
            }}
          >
            <Avatar 
              src={user.imageUrl ? getTransformedImageUrl(user.imageUrl, 'thumbnail') : undefined} 
              alt={user.name || user.username}
              sx={{ width: 40, height: 40, mr: 2 }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" color="white">
                {user.name || user.username}
              </Typography>
            </Box>
          </Box>
        )}

        <List sx={{ flexGrow: 1, p: 0, overflowY: 'auto' }}>
          {Object.entries(SECTIONS)
            .filter(([id, section]) => !section.adminOnly || (user && user.isAdmin))
            .map(([id, section]) => (
            <React.Fragment key={id}>
              <ListItem
                button
                onClick={() => handleSectionClick(id)}
                sx={{ 
                  py: 1.5,
                  backgroundColor: expandedSection === id ? 'rgba(0, 0, 0, 0.2)' : 'transparent',
                  transition: 'background-color 0.3s ease'
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40, 
                  color: 'white',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                  >
                    {id === 'chat' ? <ChatIcon /> : section.icon}
                  </motion.div>
                </ListItemIcon>
                <ListItemText 
                  primary={
                    id === 'chat' && unreadCommsCount > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography 
                          variant="body1" 
                          fontWeight={expandedSection === id ? 'medium' : 'regular'}
                          color="white"
                          sx={{ mr: 2 }}
                        >
                          {section.label}
                        </Typography>
                        <Badge
                          badgeContent={unreadCommsCount} 
                          color={unreadMentionsCount > 0 ? "primary" : "error"}
                          sx={{
                            '& .MuiBadge-badge': {
                              fontSize: '0.7rem',
                              height: 18,
                              minWidth: 18,
                            }
                          }}
                        />
                      </Box>
                    ) : section.label
                  }
                  primaryTypographyProps={{ 
                    fontWeight: expandedSection === id ? 'medium' : 'regular',
                    color: 'white'
                  }}
                  secondary={null}
                  secondaryTypographyProps={{
                    component: 'div'
                  }}
                />
                {section.subsections && section.subsections.length > 0 && (
                  <motion.div
                    animate={{ 
                      rotate: expandedSection === id ? 180 : 0 
                    }}
                    transition={{ duration: 0.3 }}
                    style={{ color: 'white' }}
                  >
                    {expandedSection === id ? <ExpandLess /> : <ExpandMore />}
                  </motion.div>
                )}
              </ListItem>
              <Collapse in={expandedSection === id} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <AnimatePresence mode="wait">
                    {section.subsections && section.subsections.map((subsection, subIndex) => (
                      <motion.div
                        key={subsection.name}
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -20, opacity: 0 }}
                        transition={{
                          duration: 0.2,
                          delay: subIndex * 0.05,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                      >
                        <ListItem
                          button
                          disabled={!subsection.route}
                          onClick={() => handleMobileSubsectionClick(subsection.route)}
                          sx={{ 
                            pl: 6,
                            py: 1.25,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: subsection.route ? 'action.hover' : 'transparent'
                            }
                          }}
                        >
                          <ListItemText 
                            primary={subsection.name} 
                            sx={{ 
                              opacity: subsection.route ? 1 : 0.5,
                              transition: 'opacity 0.3s ease',
                              m: 0
                            }} 
                            primaryTypographyProps={{ 
                              fontSize: '0.95rem',
                              color: 'white'
                            }}
                          />
                        </ListItem>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>

        {user && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            {user.isAdmin && (
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<AdminPanelSettingsIcon />}
                onClick={() => { navigate('/admin'); handleDrawerClose(); }}
                sx={{ mb: 1.5 }}
              >
                {t('admin.title')}
              </Button>
            )}
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogoutClick}
            >
              {t('auth.logout')}
            </Button>
          </Box>
        )}

        {!user && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              component={RouterLink}
              to="/login"
              onClick={handleDrawerClose}
            >
              {t('auth.login')}
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              component={RouterLink}
              to="/register"
              onClick={handleDrawerClose}
            >
              {t('auth.register')}
            </Button>
          </Box>
        )}
      </Box>
    </SwipeableDrawer>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={1}
        sx={{
          backdropFilter: 'blur(5px)',
          backgroundColor: 'rgba(18, 18, 18, 0.95)',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: 64
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isMobile ? (
              <>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{ mr: 2 }}
                  >
                    <MenuIcon />
                  </IconButton>
                </motion.div>
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    variant="h6"
                    component={RouterLink}
                    to="/"
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {!logoError && (
                      <img
                        src={siteSettings.logo}
                        alt={siteSettings.siteName}
                        style={{ height: 32, marginRight: 8 }}
                        onError={handleLogoError}
                      />
                    )}
                    {siteSettings.siteName}
                  </Typography>
                </motion.div>
              </>
            ) : (
              <>
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Typography
                    variant="h6"
                    component={RouterLink}
                    to="/"
                    sx={{
                      textDecoration: 'none',
                      color: 'inherit',
                      mr: 4,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {!logoError && (
                      <img
                        src={siteSettings.logo}
                        alt={siteSettings.siteName}
                        style={{ height: 32, marginRight: 12 }}
                        onError={handleLogoError}
                      />
                    )}
                    {siteSettings.siteName}
                  </Typography>
                </motion.div>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <AnimatePresence mode="wait">
                    {Object.entries(SECTIONS)
                      .filter(([id, section]) => !section.adminOnly || (user && user.isAdmin))
                      .map(([id, section], index) => (
                      <motion.div
                        key={id}
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -20, opacity: 0 }}
                        transition={{
                          duration: 0.3,
                          delay: index * 0.05,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                      >
                        <Tooltip key={id} title={section.label}>
                          <Button
                            color="inherit"
                            onClick={() => handleSectionClick(id)}
                            sx={{
                              position: 'relative',
                              borderBottom: activeSection === id ? 2 : 0,
                              borderColor: 'secondary.main',
                              borderRadius: 1,
                              px: 2,
                              py: 1,
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                              },
                              '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: '50%',
                                width: '0%',
                                height: '2px',
                                backgroundColor: 'secondary.main',
                                transition: 'all 0.3s ease',
                              },
                              '&:hover:after': {
                                width: '100%',
                                left: '0%',
                              }
                            }}
                          >
                            {id === 'chat' && unreadCommsCount > 0 ? (
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {section.label}
                                <Badge
                                  badgeContent={unreadCommsCount}
                                  color={unreadMentionsCount > 0 ? "primary" : "error"}
                                  sx={{
                                    ml: 2,
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.7rem',
                                      height: 18,
                                      minWidth: 18,
                                    }
                                  }}
                                />
                              </Box>
                            ) : section.label}
                          </Button>
                        </Tooltip>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </Box>
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user && (
              <>
                {user.isAdmin && !isMobile && (
                  <motion.div
                    key="admin"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Tooltip title={t('admin.title')}>
                      <IconButton
                        color="inherit"
                        onClick={() => navigate('/admin')}
                        aria-label="admin panel"
                      >
                        <AdminPanelSettingsIcon />
                      </IconButton>
                    </Tooltip>
                  </motion.div>
                )}

                {/* Chat Icon Button */}
                <motion.div
                  key="chat"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Tooltip title={t('navigation.teamChat')}>
                    <IconButton
                      color="inherit"
                      onClick={() => navigate('/communications')}
                      aria-label="team chat"
                    >
                      <Badge
                        badgeContent={unreadCommsCount > 0 ? unreadCommsCount : null}
                        color={unreadMentionsCount > 0 ? "primary" : "error"}
                        variant={unreadCommsCount > 0 ? "standard" : "dot"}
                        sx={{
                          '& .MuiBadge-badge': {
                            right: -3,
                            top: 3,
                            border: theme => `2px solid ${theme.palette.background.paper}`,
                          },
                        }}
                      >
                        <ChatIcon />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </motion.div>

                <motion.div
                  key="userProfile"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Tooltip title={user.name || user.username}>
                    <IconButton
                      onClick={handleUserMenuOpen}
                      aria-label="user profile"
                      aria-controls="user-menu"
                      aria-haspopup="true"
                    >
                      <Badge 
                        badgeContent={totalUnreadCount} 
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            right: -3,
                            top: 13,
                            border: '2px solid',
                            borderColor: 'background.paper',
                            padding: '0 4px'
                          }
                        }}
                      >
                        {user.imageUrl ? (
                          <Avatar 
                            src={user.imageUrl ? getTransformedImageUrl(user.imageUrl, 'thumbnail') : undefined} 
                            alt={user.name || user.username}
                            sx={{ width: 32, height: 32 }}
                          />
                        ) : (
                          <AccountCircle />
                        )}
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </motion.div>

                <Menu
                  id="user-menu"
                  anchorEl={userMenuAnchor}
                  keepMounted
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  TransitionComponent={Fade}
                  PaperProps={{
                    elevation: 3,
                    sx: {
                      borderRadius: 2,
                      minWidth: 200,
                      mt: 0.5
                    }
                  }}
                >
                  <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {user.name || user.username}
                    </Typography>
                  </Box>
                  <MenuItem onClick={() => { navigate('/dashboard'); handleUserMenuClose(); }}>
                    <ListItemIcon>
                      <DashboardIcon fontSize="small" />
                    </ListItemIcon>
                    {t('nav.dashboard')}
                  </MenuItem>
                  <MenuItem onClick={() => { 
                    if (unreadCount > 0) {
                      navigate('/notifications');
                    } else {
                      toast.info(t('nav.noUnreadNotifications'));
                      navigate('/notifications');
                    }
                    handleUserMenuClose(); 
                  }}>
                    <ListItemIcon>
                      <Notifications fontSize="small" />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography>{t('nav.notifications')}</Typography>
                      {unreadCount > 0 && (
                        <Chip 
                          label={unreadCount} 
                          size="small" 
                          color="error"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => { 
                    if (unreadNoticesCount > 0) {
                      navigate('/noticeboard');
                    } else {
                      toast.info(t('nav.noUnreadNotices'));
                      navigate('/noticeboard');
                    }
                    handleUserMenuClose(); 
                  }}>
                    <ListItemIcon>
                      <AnnouncementIcon fontSize="small" />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      <Typography>{t('nav.notices')}</Typography>
                      {unreadNoticesCount > 0 && (
                        <Chip 
                          label={unreadNoticesCount} 
                          size="small" 
                          color="error"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                  <MenuItem onClick={() => { 
                    navigate('/payments');
                    handleUserMenuClose(); 
                  }}>
                    <ListItemIcon>
                      <HandshakeIcon fontSize="small" />
                    </ListItemIcon>
                    <Typography>{t('navigation.payments')}</Typography>
                  </MenuItem>
                  
                  <Divider />
                  <MenuItem onClick={handleLogoutClick}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" color="error" />
                    </ListItemIcon>
                    <Typography color="error">{t('auth.logout')}</Typography>
                  </MenuItem>
                </Menu>
              </>
            )}

            <AnimatePresence mode="wait">
              {!user ? (
                <>
                  <motion.div
                    key="login"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 20, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Button
                      color="inherit"
                      component={RouterLink}
                      to="/login"
                      sx={{ 
                        mr: 1,
                        borderRadius: 4,
                        px: isMobile ? 1 : 2
                      }}
                    >
                      {t('auth.login')}
                    </Button>
                  </motion.div>
                  <motion.div
                    key="register"
                    initial={{ x: 20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 20, opacity: 0 }}
                    transition={{ duration: 0.3, delay: 0.1 }}
                  >
                    <Button
                      color="inherit"
                      component={RouterLink}
                      to="/register"
                      variant="outlined"
                      sx={{ 
                        borderRadius: 4,
                        px: isMobile ? 1 : 2
                      }}
                    >
                      {t('auth.register')}
                    </Button>
                  </motion.div>
                </>
              ) : null}
            </AnimatePresence>
          </Box>
        </Toolbar>
        {mobileDrawer}
      </AppBar>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        aria-labelledby="logout-dialog-title"
        aria-describedby="logout-dialog-description"
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            p: 1
          }
        }}
      >
        <DialogTitle id="logout-dialog-title">
          {t('auth.logoutConfirmTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-dialog-description">
            {t('auth.logoutConfirmMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel} color="primary">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleLogoutConfirm} 
            color="error" 
            variant="contained"
            autoFocus
            sx={{ borderRadius: 2 }}
          >
            {t('auth.logout')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar; 